import type {
  QueryRequestDto,
  QueryResponseDto,
  QuerySuccessDto,
} from './dto'
import { isQueryTestFailure } from './dto'
import { ApiError } from './errors'
import type { ApiContext } from './http'
import { request } from './http'
import { mapQueryOutcome } from './mappers'
import type { QueryOutcome } from './models'

/**
 * Which backend route to submit against.
 *
 * - `test`       -> POST /query/test. No auth. Gated by ENABLE_TEST_ROUTE.
 * - `production` -> POST /query.      Requires a host-issued bearer token.
 */
export type QueryRoute = 'test' | 'production'

export const QUERY_ROUTE_PATHS: Record<QueryRoute, string> = {
  test: '/query/test',
  production: '/query',
}

export const QUERY_ROUTE_LABELS: Record<QueryRoute, string> = {
  test: 'Test route (no auth)',
  production: 'Production route (bearer token)',
}

export interface SubmitQueryInput {
  query: string
  conversationId: string
  route: QueryRoute
  database?: string
  signal?: AbortSignal
}

export async function submitQuery(
  context: ApiContext,
  input: SubmitQueryInput,
): Promise<QueryOutcome> {
  const trimmed = input.query.trim()

  if (!trimmed) {
    throw new ApiError({ kind: 'client', message: 'The query is empty.' })
  }

  if (input.route === 'production' && !context.bearerToken) {
    throw new ApiError({
      kind: 'unauthorized',
      message:
        'The production route needs a bearer token. Add one in Settings, or switch back to the test route.',
    })
  }

  const body: QueryRequestDto = {
    query: trimmed,
    conversation_id: input.conversationId,
    ...(input.database?.trim() ? { database: input.database.trim() } : {}),
  }

  const payload = await request<QueryResponseDto>(context, {
    path: QUERY_ROUTE_PATHS[input.route],
    method: 'POST',
    body,
    ...(input.signal ? { signal: input.signal } : {}),
  })

  // `/query/test` reports pipeline failures with HTTP 200 and this envelope.
  if (isQueryTestFailure(payload)) {
    throw new ApiError({
      kind: 'pipeline',
      message: payload.error || 'The query pipeline failed.',
      detail: payload,
    })
  }

  const success = payload as QuerySuccessDto

  // Suggestion-based outcomes ("needs_clarification" / "failed_generic")
  // carry neither Answer nor Data by design — they must be mapped before
  // the malformed-shape check below, or they'd be misclassified as errors.
  if (success.Status && success.Status !== 'ok') {
    return mapQueryOutcome(success)
  }

  if (typeof success.Answer !== 'string' && !success.Data) {
    throw new ApiError({
      kind: 'malformed',
      message: 'The server responded in an unexpected shape.',
      detail: payload,
    })
  }

  return mapQueryOutcome(success)
}